/**
 * The routed entry points for M8 P1/P2: `/admin/*` and `/me/profile`, plus the shell's
 * account-branding hook.
 *
 * `Administration` is in the profile menu **only** for an admin (F8 §8), and navigating to
 * `/admin` directly as anyone else renders F4's 403 panel rather than an empty console
 * (P2 acceptance A9).
 */
import { PageErrorState } from '@elizmonday/ui';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTRPCClient } from '../../lib/trpc';
import { useBootstrap } from '../shell/bootstrap';
import { AdminShell, type AdminSectionId } from './AdminShell';
import { ADMIN_SECTIONS } from './AdminShell';
import { createAdminSource, type AdminTrpc } from './trpc-source';
import { createConnectionsSource, type ConnectionsTrpc } from './connections/source';
import { InviteDialog } from '../profile/InviteDialog';
import { ProfilePage, type ProfileMe, type ProfileSource } from '../profile/ProfilePage';
import './admin.css';

function useSource(): ReturnType<typeof createAdminSource> {
  const client = useTRPCClient();
  return useMemo(() => createAdminSource(client as unknown as AdminTrpc), [client]);
}

export function AdminRoute(): React.ReactElement {
  const bootstrap = useBootstrap();
  const source = useSource();
  const client = useTRPCClient();
  const connections = useMemo(() => createConnectionsSource(client as unknown as ConnectionsTrpc), [client]);
  const params = useParams();
  const navigate = useNavigate();
  const [inviting, setInviting] = useState(false);

  const wildcard = (params['*'] ?? '').split('/')[0] ?? '';
  const section = (ADMIN_SECTIONS.some((entry) => entry.id === wildcard) ? wildcard : 'users') as AdminSectionId;

  if (bootstrap.data && bootstrap.data.capabilities.administration !== true) {
    return <PageErrorState kind="forbidden" data-testid="forbidden-panel" />;
  }

  return (
    <>
      <AdminShell
        source={source}
        connections={connections}
        section={section}
        onSection={(next) => void navigate(`/admin/${next}`)}
        onInvite={() => setInviting(true)}
      />
      {inviting ? (
        <div className="ez-dialog-backdrop" data-testid="admin-invite-backdrop">
          <InviteDialog
            source={source}
            productName={bootstrap.data?.account.productName ?? 'Eli Z Monday'}
            canInviteAdmins={bootstrap.data?.me.role === 'admin'}
            onClose={() => setInviting(false)}
          />
        </div>
      ) : null}
    </>
  );
}

/** `/me/profile` (M8-P2 §6.11). */
export function ProfileRoute(): React.ReactElement {
  const client = useTRPCClient();
  const source = useMemo<ProfileSource>(() => {
    const api = client as unknown as {
      profile: {
        get: { query(input?: undefined): Promise<ProfileMe> };
        update: { mutate(patch: Record<string, unknown>): Promise<ProfileMe> };
        changePassword: {
          mutate(input: { currentPassword: string; newPassword: string }): Promise<unknown>;
        };
      };
      auth: {
        sessions: {
          list: {
            query(input?: undefined): Promise<
              Array<{
                id: string;
                ip: string | null;
                userAgent: string | null;
                lastSeenAt: string | null;
                current: boolean;
              }>
            >;
          };
          revoke: { mutate(input: { id: string }): Promise<unknown> };
        };
        logoutAll: { mutate(input: { keepCurrent: boolean }): Promise<{ revoked: number }> };
      };
    };
    return {
      me: () => api.profile.get.query(undefined),
      update: (patch) => api.profile.update.mutate(patch),
      changePassword: (input) => api.profile.changePassword.mutate(input),
      sessions: () => api.auth.sessions.list.query(undefined),
      revokeSession: (id) => api.auth.sessions.revoke.mutate({ id }),
      logoutOthers: () => api.auth.logoutAll.mutate({ keepCurrent: true }),
    };
  }, [client]);

  return <ProfilePage source={source} />;
}
